import * as _ from "lodash";
import moment, { ISO_8601 } from "moment";
import "moment/locale/vi";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import FieldInput from "../../components/form/FieldInput";
import TIcon from "../../components/ui/TIcon";
import "../league/shared/league.css";
import * as SBService from "../../helpers/sportbookService";
import { setAnalyticsUser } from "../../helpers/analytics";
import { getAssetFullUrl, toCurrency } from "../../helpers/utils";
import {
  bottomBarState,
  loaderState,
  showBottomBarState,
  userState,
} from "../../states/common";

import * as Push from "../../helpers/push";
import { huongDanCaiDat } from "../../helpers/huongDanCai";

/** Lời giải thích cho từng lý do không bật được, viết cho người dùng chứ không
 *  cho lập trình viên: mỗi câu phải nói rõ họ làm gì tiếp theo. */
const VI_SAO: Record<Push.LyDoKhongDuoc, string> = {
  // "chua-cai-pwa" không dùng ở đây: iPhone chưa cài thì mở hẳn màn hướng dẫn,
  // vì một dòng chữ không đủ để ai đó làm theo được.
  "chua-cai-pwa": "",
  "trinh-duyet": "Trình duyệt này chưa hỗ trợ thông báo đẩy. Thử mở Biadi bằng Chrome hoặc Safari.",
  "bi-chan":
    "Bạn đã chặn thông báo của Biadi. Mở phần cài đặt của trình duyệt cho trang này và cho phép Thông báo.",
  "chua-cau-hinh": "Thông báo chưa được bật cho bản này. Báo lại ban quản trị giúp nhé.",
  "khong-lay-duoc-ma":
    "Không lấy được mã thiết bị từ Firebase. Kiểm tra mạng rồi thử lại.",
  "may-chu-tu-choi":
    "Máy chủ chưa nhận được đăng ký. Đây là lỗi phía Biadi, không phải máy bạn — báo lại ban quản trị giúp nhé.",
  "that-bai": "Không bật được thông báo. Thử lại giúp nhé.",
};

const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(-2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

/** Màn 05 — Tài khoản.
 *  Thông tin, số liệu và lịch sử thi đấu lấy từ API thật; tiến trình hạng cơ
 *  suy ra từ điểm hiện tại vì backend chưa trả ngưỡng lên hạng. */
const ProfilePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useRecoilState(userState);
  const [, setLoader] = useRecoilState(loaderState);
  const [, setActive] = useRecoilState(bottomBarState);
  const [, setBottomBar] = useRecoilState(showBottomBarState);

  const [matches, setMatches] = useState<any[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [saving, setSaving] = useState(false);
  /* Công tắc này TỪNG chỉ ghi vào localStorage - bật hay tắt đều không có thông
     báo nào cả. Giờ nó là quyền thật của trình duyệt cộng với một mã thiết bị
     đăng ký ở server, nên trạng thái ban đầu phải đọc từ hai chỗ đó, không phải
     từ một cái cờ của riêng máy này. */
  const [notifyMatch, setNotifyMatch] = useState(
    () => Push.dangBat() && Push.quyenHienTai() === "granted"
  );
  const [notifyBusy, setNotifyBusy] = useState(false);
  /* iPhone chưa thêm vào Màn hình chính: mở màn hướng dẫn thay vì một dòng toast. */
  const [huongDanCai, setHuongDanCai] = useState(false);

  useEffect(() => {
    document.title = "Biadi — Tài khoản";
    setActive("profile");
    setBottomBar(true);

    const load = async () => {
      setLoader(true);
      try {
        const rsp = await SBService.myProfile();
        const profile = rsp && rsp.data ? rsp.data : rsp;
        if (!profile) {
          navigate("/auth/login");
          return;
        }
        profile.profilePicture = profile.profilePicture
          ? getAssetFullUrl(profile.profilePicture)
          : "";
        setUser(profile);
        setAnalyticsUser(profile.id)

        const matchRsp = await SBService.getMyMatch(20, 1).catch(() => null);
        setMatches(matchRsp?.data ?? []);
      } catch (e: any) {
        toast.error(
          e?.response?.data?.error ?? "Không thể tải thông tin tài khoản"
        );
        navigate("/auth/login");
      }
      setLoader(false);
    };
    load();
  }, []);

  const stats = useMemo(() => {
    const played = matches.filter((m) => !_.isEmpty(m));
    const won = played.filter((m) => {
      const isChallenger = m.challenger?.id === user.id;
      const mine = isChallenger ? m.challengerPoint : m.acceptorPoint;
      const theirs = isChallenger ? m.acceptorPoint : m.challengerPoint;
      return (
        typeof mine === "number" && typeof theirs === "number" && mine > theirs
      );
    }).length;
    const rate = played.length ? Math.round((won / played.length) * 100) : 0;
    return { total: played.length, won, rate };
  }, [matches, user.id]);

  /* Tiến trình lên hạng: backend chưa có ngưỡng, tạm dùng mốc tròn kế tiếp. */
  const progress = useMemo(() => {
    const score = Number(user.score ?? 0);
    const target = Math.max(1000, Math.ceil((score + 1) / 1000) * 1000);
    return {
      score,
      target,
      remain: target - score,
      percent: Math.min(100, Math.round((score / target) * 100)),
    };
  }, [user.score]);

  const toggleNotify = async () => {
    if (notifyBusy) {
      return;
    }
    setNotifyBusy(true);
    try {
      if (notifyMatch) {
        await Push.tatThongBao();
        setNotifyMatch(false);
        toast.success("Đã tắt thông báo trên máy này");

        return;
      }
      const ra = await Push.batThongBao();
      if (ra.xong) {
        setNotifyMatch(true);
        toast.success("Đã bật thông báo");
      } else if (ra.vi === "chua-cai-pwa") {
        /* Trình duyệt nào mời cài được bằng một cú bấm thì mời luôn; iPhone không
           có API nào như thế nên chỉ còn cách chỉ từng bước. */
        setNotifyMatch(false);
        if (Push.coTheMoiCai()) {
          const dongY = await Push.moiCaiApp();
          if (!dongY) {
            setHuongDanCai(true);
          }
        } else {
          setHuongDanCai(true);
        }
      } else {
        /* Giữ công tắc ở TẮT khi không bật được. Gạt sang bật rồi báo lỗi là nói
           dối: người dùng đóng thông báo lỗi và tưởng mình đã bật xong. */
        setNotifyMatch(false);
        toast.error(VI_SAO[ra.vi], { duration: 6000 });
      }
    } finally {
      setNotifyBusy(false);
    }
  };

  /**
   * Giải ĐANG tham gia = đã đăng ký và chưa qua ngày kết thúc.
   *
   * user.leagues là toàn bộ lịch sử đăng ký, nên trước đây khối này liệt kê cả
   * giải đã xong — cùng một tiêu đề mà nội dung lại là chuyện khác.
   *
   * Thiếu endDate thì giữ lại: không phán được thì hiện thừa vẫn hơn là giấu
   * mất một giải người dùng đang thi đấu.
   */
  const activeLeagues = useMemo(
    () =>
      (user.leagues ?? [])
        .filter(
          (lg: any) => !lg.endDate || moment(lg.endDate, ISO_8601).endOf("day").isAfter(moment())
        )
        /* Mới nhất trước, đo bằng ngày kết thúc gần nhất — cùng thứ tự với tab
           "Đang tham gia" bên trang Giải đấu, để bấm "Tất cả" không thấy một
           danh sách xếp khác hẳn. Giải thiếu endDate xuống cuối vì không xếp
           được, chứ không phải bị coi là cũ nhất. */
        .sort((a: any, b: any) => {
          if (!a.endDate) return 1;
          if (!b.endDate) return -1;
          return moment(b.endDate, ISO_8601).valueOf() - moment(a.endDate, ISO_8601).valueOf();
        }),
    [user.leagues]
  );

  /** Tài khoản tạo bằng Google: email do Google quản lý, màn này không cho sửa. */
  const laTaiKhoanGoogle = user.provider === "google";

  /*
   * Dấu tích xanh cạnh ô email.
   *
   * Dựa vào emailVerifiedAt chứ KHÔNG dựa vào isVerified: isVerified là trạng
   * thái của tài khoản và nó đúng sai lẫn lộn ở đây - tài khoản tạo trước luồng
   * OTP có isVerified = true chỉ vì endpoint xác nhận ngày trước nhận mọi mã.
   * Gắn tích xanh cho một email chưa ai kiểm là nói dối người dùng.
   *
   * Form sửa thông tin đổi email thì tích tắt ngay, không chờ bấm Lưu: email vừa
   * gõ vào rõ ràng chưa được xác thực.
   */
  const emailDaXacThuc =
    Boolean(user.emailVerifiedAt) &&
    Boolean(form.email.trim()) &&
    form.email.trim().toLowerCase() === (user.email ?? "").toLowerCase();

  const openEdit = () => {
    setForm({
      name: user.name ?? "",
      phone: user.phone ?? "",
      email: user.email ?? "",
    });
    setEditOpen(true);
  };

  const saveProfile = async () => {
    if (!form.name.trim()) {
      toast.error("Tên không được để trống");
      return;
    }
    // Chỉ gửi trường thật sự đổi: backend kiểm tra trùng số điện thoại và
    // email, gửi lại giá trị cũ là tự báo "đã tồn tại" với chính mình.
    const payload: any = {};
    if (form.name.trim() !== (user.name ?? "")) payload.name = form.name.trim();
    if (form.phone.trim() !== (user.phone ?? "")) payload.phone = form.phone.trim();
    if (!laTaiKhoanGoogle && form.email.trim() !== (user.email ?? "")) {
      payload.email = form.email.trim();
    }

    if (Object.keys(payload).length === 0) {
      setEditOpen(false);
      return;
    }

    setSaving(true);
    try {
      await SBService.updateMyProfile(payload);
      setUser({ ...user, ...payload });
      toast.success("Đã cập nhật thông tin");
      setEditOpen(false);
    } catch (e: any) {
      const code = e?.response?.data?.error;
      const messages: Record<string, string> = {
        phoneAlreadyExists: "Số điện thoại đã có người dùng",
        emailAlreadyExists: "Email đã có người dùng",
        emailNotEditableForGoogleAccount:
          "Email của tài khoản Google do Google quản lý, không sửa được",
        mustBeNotEmpty: "Vui lòng điền đủ thông tin",
      };
      toast.error(messages[code] || "Không cập nhật được, vui lòng thử lại");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="page-head">
        <h1>TÀI KHOẢN</h1>
        <button type="button" className="icon-btn" aria-label="Thông báo">
          <TIcon name="bell" />
        </button>
      </header>

      {/* ── Thẻ hồ sơ ── */}
      <section className="ui-card profile-card">
        <div className="who">
          <span
            className="avatar avatar-ring"
            style={{ width: 72, height: 72, fontSize: 18 }}
          >
            {user.profilePicture ? (
              <img src={user.profilePicture} alt="" />
            ) : (
              initials(user.name || "?")
            )}
          </span>

          <div className="id">
            <span className="name">{user.name || "—"}</span>
            <span className="handle">
              {user.phone}
              {user.email ? ` · ${user.email}` : ""}
            </span>
            <div className="tags">
              <span className="tag-solid">
                HẠNG CƠ {user.ranking?.code ?? "--"}
              </span>
              <span className="tag-ghost">
                {(user.ranking?.class ?? "CHƯA XẾP HẠNG").toUpperCase()}
              </span>
              {!user.isCalibrated ? (
                <span className="tag-ghost">CHƯA THẨM ĐỊNH</span>
              ) : null}
            </div>
          </div>

          {/* Trước đây nút này mở /ranking/:id — màn dành cho chuyên gia đánh
              giá trình độ — nên người chơi tự sửa được hạng của chính mình. */}
          <button
            type="button"
            style={{ color: "var(--biadi-steel)" }}
            aria-label="Chỉnh sửa thông tin cá nhân"
            onClick={openEdit}
          >
            <TIcon name="pencil" className="text-[18px]" />
          </button>
        </div>

        <div className="stats">
          <div className="stat">
            <span className="v">{stats.total}</span>
            <span className="k">TRẬN</span>
          </div>
          <div className="stat">
            <span className="v hot">{stats.won}</span>
            <span className="k">THẮNG</span>
          </div>
          <div className="stat">
            <span className="v">{stats.rate}%</span>
            <span className="k">TỶ LỆ THẮNG</span>
          </div>
          <div className="stat">
            <span className="v">{toCurrency(progress.score)}</span>
            <span className="k">ĐIỂM</span>
          </div>
        </div>
      </section>

      {/* ── Tiến trình hạng cơ ── */}
      <section className="ui-card progress-card">
        <div className="head">
          <span className="label">TIẾN TRÌNH HẠNG CƠ</span>
          <span className="target">
            {user.ranking?.code ?? "--"} → {progress.target / 1000}K
          </span>
        </div>
        <div className="progress-track">
          <span
            className="progress-fill"
            style={{ width: `${progress.percent}%` }}
          />
        </div>
        <span className="hint">
          Cần thêm {toCurrency(progress.remain)} điểm tích luỹ để đạt mốc kế
          tiếp.
        </span>
      </section>

      {/* ── Lịch sử thi đấu ── */}
      <section className="section" style={{ paddingTop: 24, gap: 12 }}>
        <div className="section-head">
          <h2>LỊCH SỬ THI ĐẤU</h2>
          <button type="button" onClick={() => navigate("/profile/rank-history")}>
            Tất cả <TIcon name="chevron-right" />
          </button>
        </div>

        <div className="ui-card list">
          {matches.length ? (
            matches.slice(0, 5).map((m: any, i: number) => {
              const isChallenger = m.challenger?.id === user.id;
              const opponent = isChallenger ? m.acceptor : m.challenger;
              const mine = isChallenger ? m.challengerPoint : m.acceptorPoint;
              const theirs = isChallenger ? m.acceptorPoint : m.challengerPoint;
              const won = mine > theirs;
              return (
                <div
                  key={m.id ?? i}
                  className="list-row match-row"
                  style={{ padding: "13px 16px" }}
                >
                  <span className={`result-chip ${won ? "win" : "loss"}`}>
                    {won ? "T" : "B"}
                  </span>
                  <div className="info">
                    <span className="vs">
                      vs {opponent?.name ?? "—"}
                      {m.league?.name ? ` · ${m.league.name}` : ""}
                    </span>
                    <span className="meta">
                      {m.endedAt
                        ? moment(m.endedAt, ISO_8601).format("DD/MM/YYYY")
                        : "Chưa kết thúc"}
                    </span>
                  </div>
                  <span
                    className={`score ${won ? "delta-up" : "delta-down"}`}
                  >
                    {mine ?? 0}–{theirs ?? 0}
                  </span>
                </div>
              );
            })
          ) : (
            <div className="empty-state">
              <TIcon name="ball-baseball" />
              <div className="t">Chưa có trận nào</div>
              <div className="h">Tham gia một giải để bắt đầu tích điểm</div>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                style={{ marginTop: 14 }}
                onClick={() => navigate("/league")}
              >
                XEM GIẢI ĐANG DIỄN RA
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── Giải đang tham gia ── */}
      {activeLeagues.length > 0 ? (
        <section className="section" style={{ paddingTop: 24, gap: 12 }}>
          <div className="section-head">
            <h2>GIẢI ĐANG THAM GIA</h2>
            {/* Chỉ có nút khi còn giải chưa hiện ở đây; dẫn đúng tab đang xem,
                không phải tab mặc định của trang giải. */}
            {activeLeagues.length > 3 ? (
              <button type="button" onClick={() => navigate("/league?tab=joined")}>
                Tất cả <TIcon name="chevron-right" />
              </button>
            ) : null}
          </div>
          <div className="ui-card list">
            {activeLeagues.slice(0, 3).map((lg: any, i: number) => (
              <button
                key={lg.id ?? i}
                type="button"
                className="list-row setting-row"
                onClick={() => navigate(`/league/${lg.id}/detail`)}
              >
                <TIcon name="trophy" />
                <span className="label">{lg.name ?? "Giải đấu"}</span>
                <TIcon name="chevron-right" />
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {/* ── Cài đặt ── */}
      <section
        className="section"
        style={{ paddingTop: 24, paddingBottom: 30, gap: 12 }}
      >
        <div className="section-head">
          <h2>CÀI ĐẶT</h2>
        </div>
        <div className="ui-card list">
          <button
            type="button"
            className="list-row setting-row"
            onClick={() => navigate("/my-qr")}
          >
            <TIcon name="qrcode" />
            <span className="label">Mã QR của tôi</span>
            <TIcon name="chevron-right" />
          </button>

          <div className="list-row setting-row">
            <TIcon name="bell" />
            <span className="label">Thông báo trận đấu</span>
            <button
              type="button"
              className={`toggle${notifyMatch ? " on" : ""}`}
              aria-label="Bật/tắt thông báo trận đấu"
              aria-pressed={notifyMatch}
              disabled={notifyBusy}
              onClick={toggleNotify}
            />
          </div>

          <button
            type="button"
            className="list-row setting-row"
            onClick={() => navigate("/profile/rank-history")}
          >
            <TIcon name="history" />
            <span className="label">Lịch sử điểm hạng</span>
            <TIcon name="chevron-right" />
          </button>

          <button
            type="button"
            className="list-row setting-row"
            /* Mang email sẵn có sang để người dùng không phải gõ lại - và để
               họ không gõ nhầm sang một email khác rồi nhận "email chưa đăng
               ký" mà không hiểu vì sao.

               doiMatKhau: màn đích dùng chung với "Quên mật khẩu?" của trang
               đăng nhập. Không có cờ này thì người đang đăng nhập bấm "Đổi mật
               khẩu" lại rơi vào một màn ghi "Khôi phục mật khẩu" và tưởng mình
               bấm nhầm. */
            onClick={() =>
              navigate("/auth/forgot-password", {
                state: { email: user.email ?? "", doiMatKhau: true },
              })
            }
          >
            <TIcon name="shield-lock" />
            <span className="label">Đổi mật khẩu</span>
            <TIcon name="chevron-right" />
          </button>

          <button
            type="button"
            className="list-row setting-row danger"
            onClick={() => navigate("/auth/logout")}
          >
            <TIcon name="logout" />
            <span className="label">Đăng xuất</span>
          </button>
        </div>
      </section>

      {/* ── Sửa thông tin cá nhân ──
           Ảnh đại diện cố ý không nằm ở đây: đổi ảnh cần upload base64 và một
           luồng riêng, còn ba trường này chỉ là text. */}
      {/* ── Hướng dẫn thêm Biadi vào Màn hình chính ──
           iOS chỉ cho web push khi app đã nằm ở Màn hình chính (từ iOS 16.4), và
           Apple KHÔNG có API nào để tự mở hộp thoại cài - chỉ thêm tay được.

           Các bước KHÁC NHAU theo từng trình duyệt, và Safari còn đổi chỗ giữa
           các bản iOS, nên hướng dẫn phải dựng theo máy đang dùng. Chỉ sai một
           bước là người dùng bấm quanh không ra rồi bỏ cuộc. */}
      {huongDanCai ? (
        (() => {
          const hd = huongDanCaiDat();

          return (
            <div className="biadi">
              <div className="sheetwrap">
                <div className="sheetbd" onClick={() => setHuongDanCai(false)} />
                <div className="sheet">
                  <div className="grab" />
                  <div className="sc">
                    <div className="sttl">Thêm Biadi vào Màn hình chính</div>
                    <p className="small" style={{ marginTop: 6 }}>
                      iPhone chỉ gửi được thông báo cho app đã nằm ở Màn hình chính. Thêm một
                      lần, dùng mãi — và Biadi sẽ mở toàn màn hình, không còn thanh địa chỉ.
                    </p>

                    {hd.chuaChacNhanDuocThongBao ? (
                      <div className="caidat-luuy">
                        <b>Nên mở bằng Safari.</b> Trên iPhone, chỉ app thêm từ Safari mới
                        chắc chắn nhận được thông báo. Thêm từ {hd.ten} vẫn tạo được biểu
                        tượng, nhưng có thể không nhận được — nếu thêm xong mà vẫn không bật
                        được thông báo, hãy mở trang này bằng Safari rồi làm lại.
                      </div>
                    ) : null}

                    <div className="caidat-ten">Các bước trong {hd.ten}</div>
                    <ol className="caidat-buoc">
                      {hd.buoc.map((buoc) => (
                        <li key={buoc}>{buoc}</li>
                      ))}
                    </ol>

                    <button
                      type="button"
                      className="btn btn-primary btn-block"
                      style={{ marginTop: 16 }}
                      onClick={() => setHuongDanCai(false)}
                    >
                      ĐÃ HIỂU
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()
      ) : null}

      {editOpen ? (
        <div className="biadi">
          <div className="sheetwrap">
            <div className="sheetbd" onClick={() => setEditOpen(false)} />
            <div className="sheet">
            <div className="grab" />
            <div className="sc">
              <div className="sttl">Thông tin cá nhân</div>

              <div className="edit-fields">
              <FieldInput
                label="HỌ VÀ TÊN"
                name="name"
                icon="user"
                placeholder="Nguyễn Văn A"
                value={form.name}
                onChange={(name, e) => setForm({ ...form, name: e.target.value })}
              />

              <FieldInput
                label="SỐ ĐIỆN THOẠI"
                name="phone"
                type="tel"
                icon="phone"
                placeholder="0912345678"
                value={form.phone}
                onChange={(name, e) => setForm({ ...form, phone: e.target.value })}
              />
              <p className="tiny" style={{ marginTop: -8, marginBottom: 12 }}>
                Số này cũng là tên đăng nhập của bạn.
              </p>

              {/* Tài khoản Google: email là khoá liên kết với Google, đổi đi là
                  tự cắt đường đăng nhập của chính mình. Backend cũng chặn, đây
                  chỉ là để người dùng không gõ vào một ô rồi nhận lỗi. */}
              <FieldInput
                label="EMAIL"
                name="email"
                type="email"
                icon="mail"
                placeholder="ban@example.com"
                value={form.email}
                disabled={laTaiKhoanGoogle}
                verified={emailDaXacThuc}
                hint={
                  laTaiKhoanGoogle
                    ? "Email do Google cung cấp, không sửa được."
                    : emailDaXacThuc
                    ? "Email đã xác thực. Đổi sang email khác sẽ phải xác thực lại."
                    : "Email chưa xác thực. Dùng mục Đổi mật khẩu để xác thực bằng mã gửi qua email."
                }
                onChange={(name, e) => setForm({ ...form, email: e.target.value })}
              />
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                  disabled={saving}
                  onClick={saveProfile}
                >
                  {saving ? "Đang lưu…" : "Lưu"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  style={{ flex: 1 }}
                  disabled={saving}
                  onClick={() => setEditOpen(false)}
                >
                  Huỷ
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ProfilePage;
